import { useMemo } from "react";
import {
  getTodayRun,
  getCurrentWeek,
  getRaceCountdown,
  getThisWeekRuns,
  getTodaySmartSuggestion,
  getRunFeedback,
  getWeekMileageProgress,
  getWeekCompletionMap,
} from "../utils/run-plan";
import {
  getAdaptivePace,
  getSmartCoachMessage,
  getInjuryRisk,
} from "../utils/run-intelligence";

const RUN_TYPE_COLORS = {
  Easy: "#7EB8A4",
  Recovery: "#B5B5B0",
  Tempo: "#D4A04A",
  Intervals: "#D88BAE",
  "Long Run": "#64B5F6",
  "Race Pace": "#E57373",
};

const RISK_STYLES = {
  warning: { bg: "rgba(229, 115, 115, 0.08)", color: "#E57373", icon: "!" },
  caution: { bg: "rgba(212, 160, 74, 0.08)", color: "#D4A04A", icon: "~" },
  recovery: { bg: "rgba(126, 184, 164, 0.08)", color: "#7EB8A4", icon: "+" },
  celebration: { bg: "rgba(126, 184, 164, 0.08)", color: "#7EB8A4", icon: "\u2713" },
  momentum: { bg: "rgba(100, 181, 246, 0.08)", color: "#64B5F6", icon: "\u2192" },
};

export default function TodayRunCard({ plan, runs, onLogRun, onViewPlan }) {
  const todayRun = useMemo(() => getTodayRun(plan), [plan]);
  const currentWeek = useMemo(() => getCurrentWeek(plan), [plan]);
  const countdown = useMemo(() => getRaceCountdown(plan), [plan]);
  const weekRuns = useMemo(() => getThisWeekRuns(plan), [plan]);
  const suggestion = useMemo(() => getTodaySmartSuggestion(plan, runs), [plan, runs]);
  const mileageProgress = useMemo(() => getWeekMileageProgress(plan, runs), [plan, runs]);
  const completionMap = useMemo(() => getWeekCompletionMap(plan, runs), [plan, runs]);

  // Smart coaching
  const adaptivePace = useMemo(
    () => suggestion.run ? getAdaptivePace(runs, suggestion.run.type) : null,
    [runs, suggestion.run]
  );
  const coachMessage = useMemo(() => getSmartCoachMessage(plan, runs), [plan, runs]);
  const risk = useMemo(() => getInjuryRisk(plan, runs), [plan, runs]);

  const totalWeeks = plan.plan?.total_weeks || 0;
  const weekData = plan.plan?.weeks?.find((w) => w.week_number === currentWeek);

  // Feedback for completed run
  const feedback = useMemo(() => {
    if (suggestion.type !== "completed" || !suggestion.loggedRun) return null;
    return getRunFeedback(suggestion.run, suggestion.loggedRun);
  }, [suggestion]);

  // Find next upcoming run this week
  const nextRun = useMemo(() => {
    if (!weekRuns.length) return null;
    const todayIdx = (new Date().getDay() + 6) % 7;
    const dayOrder = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
    return weekRuns.find((r) => {
      const rIdx = dayOrder.indexOf(r.day);
      return rIdx > todayIdx && r.type !== "Rest" && r.type !== "Cross Train";
    });
  }, [weekRuns]);

  const scheduledCount = weekRuns.filter((r) => r.type !== "Rest" && r.type !== "Cross Train").length;
  const completedCount = Object.values(completionMap).filter((v) => v.completed).length;
  const mileagePct = mileageProgress.planned > 0
    ? Math.min(100, Math.round((mileageProgress.actual / mileageProgress.planned) * 100))
    : 0;

  const accentColor = todayRun ? (RUN_TYPE_COLORS[todayRun.type] || "var(--accent)") : "var(--surface3)";

  return (
    <div
      className="card"
      style={{
        padding: "16px 20px",
        marginBottom: 16,
        borderLeft: `3px solid ${
          risk.level === "high" ? "#E57373" :
          suggestion.type === "makeUp" ? "#D4A04A" : accentColor
        }`,
      }}
    >
      {/* Header */}
      <div className="row-between mb-8">
        <div className="section-label" style={{ marginBottom: 0 }}>
          {plan.race_type || "Run"} Training
        </div>
        <div className="text-xs" style={{ color: "var(--text3)" }}>
          Week {currentWeek}/{totalWeeks} &middot; {weekData?.phase || ""}
        </div>
      </div>

      {/* ─── Smart coach message ─── */}
      {coachMessage && (
        <div
          style={{
            background: RISK_STYLES[coachMessage.type]?.bg || "rgba(100, 181, 246, 0.08)",
            padding: "8px 10px",
            borderRadius: 6,
            marginBottom: 12,
            display: "flex",
            gap: 8,
            alignItems: "flex-start",
          }}
        >
          <span style={{
            color: RISK_STYLES[coachMessage.type]?.color || "var(--text2)",
            fontWeight: 600,
            fontSize: 13,
            lineHeight: "18px",
            flexShrink: 0,
          }}>
            {RISK_STYLES[coachMessage.type]?.icon || "\u2022"}
          </span>
          <div className="text-xs" style={{
            color: RISK_STYLES[coachMessage.type]?.color || "var(--text2)",
            lineHeight: 1.5,
          }}>
            {coachMessage.message}
          </div>
        </div>
      )}

      {/* ─── Main content based on smart suggestion ─── */}

      {suggestion.type === "completed" ? (
        /* Today's run is done — show feedback */
        <div>
          <div className="row-between" style={{ alignItems: "baseline" }}>
            <div style={{ fontSize: 18, fontWeight: 400, color: "var(--success)" }}>
              {suggestion.run.type} — Done
            </div>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--success)" strokeWidth="2.5" strokeLinecap="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          {feedback ? (
            <div style={{ marginTop: 8 }}>
              <div className="mono-sm" style={{ color: "var(--text2)" }}>
                {feedback.actualDist} mi logged
                {feedback.plannedDist > 0 && ` / ${feedback.plannedDist} mi planned`}
              </div>
              <div className="text-sm mt-4" style={{ color: feedback.verdict === "nailed" ? "var(--success)" : "var(--text2)", lineHeight: 1.5 }}>
                {feedback.message}
              </div>
            </div>
          ) : (
            <div className="text-sm" style={{ color: "var(--text2)" }}>
              {suggestion.run.distance} mi completed
            </div>
          )}
        </div>

      ) : suggestion.type === "makeUp" ? (
        /* Rest day with missed run — offer make-up */
        <div>
          <div style={{ fontSize: 18, fontWeight: 400, color: "#D4A04A", marginBottom: 4 }}>
            Make Up: {suggestion.run.type}
          </div>
          <div className="text-sm" style={{ color: "var(--text2)", lineHeight: 1.5, marginBottom: 8 }}>
            {suggestion.message}
          </div>
          <div className="mono-sm" style={{ color: "var(--text2)", marginBottom: 12 }}>
            Suggested: {suggestion.adjustedDistance} mi (originally {suggestion.run.distance} mi)
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              className="btn btn-primary btn-h44"
              style={{ flex: 1 }}
              onClick={() => onLogRun?.({
                ...suggestion.run,
                distance: suggestion.adjustedDistance,
                _isMakeUp: true,
                _originalDay: suggestion.originalDay,
              })}
            >
              Log Make-Up Run
            </button>
            <button
              className="btn btn-secondary btn-h44"
              style={{ padding: "0 16px" }}
              onClick={() => onLogRun?.(null)}
            >
              Skip
            </button>
          </div>
        </div>

      ) : suggestion.type === "rest" ? (
        /* Normal rest day */
        <div>
          <div style={{ fontSize: 18, fontWeight: 400, marginBottom: 4 }}>Rest Day</div>
          <div className="text-sm" style={{ color: "var(--text2)" }}>
            {nextRun
              ? `Next up: ${nextRun.type} — ${nextRun.distance} mi on ${nextRun.day}`
              : "Recovery is part of the training. Your body builds strength while you rest."}
          </div>
        </div>

      ) : suggestion.type === "crossTrain" ? (
        /* Cross training day */
        <div>
          <div style={{ fontSize: 18, fontWeight: 400, marginBottom: 4 }}>Cross Training</div>
          <div className="text-sm" style={{ color: "var(--text2)" }}>
            {todayRun?.description || "Swimming, cycling, yoga, or strength work. Keep it moderate."}
          </div>
        </div>

      ) : (
        /* Scheduled run — not yet done */
        <div>
          <div className="row-between" style={{ alignItems: "baseline" }}>
            <div style={{ fontSize: 18, fontWeight: 400 }}>
              {suggestion.run.type}
            </div>
            <div className="mono" style={{ fontSize: 16, color: RUN_TYPE_COLORS[suggestion.run.type] || "var(--text)" }}>
              {suggestion.run.distance} mi
            </div>
          </div>

          {/* Adaptive pace — personalized from your data, overrides plan default */}
          {adaptivePace ? (
            <div style={{ marginTop: 6 }}>
              <div className="mono-sm" style={{ color: RUN_TYPE_COLORS[suggestion.run.type] || "var(--accent)" }}>
                {adaptivePace.display}
              </div>
              <div className="text-xs" style={{ color: "var(--text3)", marginTop: 2 }}>
                {adaptivePace.label}
              </div>
            </div>
          ) : suggestion.run.pace_guidance ? (
            <div className="mono-sm mt-2" style={{ color: "var(--text2)" }}>
              {suggestion.run.pace_guidance}
            </div>
          ) : null}

          {suggestion.run.description && (
            <div className="text-sm mt-6" style={{ color: "var(--text2)", lineHeight: 1.5 }}>
              {suggestion.run.description}
            </div>
          )}

          {/* Missed run nudge */}
          {suggestion.missedRun && (
            <div
              className="text-xs mt-8"
              style={{
                color: "#D4A04A",
                background: "rgba(212, 160, 74, 0.08)",
                padding: "8px 10px",
                borderRadius: 6,
                lineHeight: 1.5,
              }}
            >
              {suggestion.message}
            </div>
          )}

          <button
            className="btn btn-primary btn-h44 w-full mt-12"
            onClick={() => onLogRun?.(suggestion.run)}
          >
            Log This Run
          </button>
        </div>
      )}

      {/* ─── Week mileage progress bar ─── */}
      {mileageProgress.planned > 0 && (
        <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid var(--border)" }}>
          <div className="row-between mb-4">
            <div className="text-xs" style={{ color: "var(--text3)" }}>
              Week mileage
            </div>
            <div className="mono" style={{ fontSize: 11, color: "var(--text2)" }}>
              {mileageProgress.actual} / {mileageProgress.planned} mi
            </div>
          </div>
          <div style={{
            width: "100%",
            height: 4,
            borderRadius: 2,
            background: "var(--surface3)",
          }}>
            <div style={{
              width: `${mileagePct}%`,
              height: "100%",
              borderRadius: 2,
              background: mileagePct >= 90 ? "var(--success)" : "var(--accent)",
              transition: "width 0.4s ease",
            }} />
          </div>
          <div className="text-xs mt-4" style={{ color: "var(--text3)" }}>
            {completedCount}/{scheduledCount} runs &middot;{" "}
            {mileagePct >= 100
              ? "Week target hit!"
              : mileagePct >= 70
                ? "On track"
                : mileagePct >= 40
                  ? "Building up"
                  : "Just getting started"}
          </div>
        </div>
      )}

      {/* Race countdown */}
      {countdown && (
        <div
          style={{
            marginTop: 12,
            paddingTop: 12,
            borderTop: "1px solid var(--border)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div className="text-xs" style={{ color: "var(--text3)" }}>
            {plan.race_type} in {countdown.days} days
          </div>
          <button
            className="text-xs"
            style={{
              background: "none",
              border: "none",
              color: "var(--accent)",
              cursor: "pointer",
              fontWeight: 500,
            }}
            onClick={onViewPlan}
          >
            View Plan
          </button>
        </div>
      )}
    </div>
  );
}
